import React, { useCallback, useMemo, useRef } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { useHasRole } from "../../hooks/useHasRole";
import { useThgAnnualSummary } from "./hooks/useThgAnnualSummary";
import { availableYears, defaultYear } from "./utils/thgYears";
import ThgPricesButton from "../ThgPrices/ThgPricesButton";
import SummaryHeader from "./components/SummaryHeader";
import ProvisionalBanner from "./components/ProvisionalBanner";
import DataQualityNotes from "./components/DataQualityNotes";
import ThgStationTable from "./components/ThgStationTable";
import ThgExportButton from "./components/ThgExportButton";
import RecomputeButton from "./components/RecomputeButton";
import ThgFailureState from "./components/ThgFailureState";
import PayoutTotals from "./components/PayoutTotals";
import PayoutConfirmDialog from "./components/PayoutConfirmDialog";
import SyncButton from "./components/SyncButton";
import { PayoutNotification, useThgPayoutActions } from "./hooks/useThgPayoutActions";
import { hasPayoutData } from "./utils/thgPayout";
import { isUnpaidInternal } from "./utils/thgInternal";
import { COPY, PAYOUT_COPY } from "./ThgAnnualSummary.consts";

/**
 * CLR-281 THG payouts: one row per station with its owner and THG payout for a calendar year, so finance
 * can pay each station owner their THG share.
 *
 * A payout list, not an analytics view: every figure is the backend's, shown at fixed precision and never
 * recalculated. The page opens on the most recent FINAL year; the current year can be previewed, and is
 * marked provisional everywhere it appears so it is never paid from.
 */
const ThgAnnualSummary: React.FC = () => {
  const toast = useRef<Toast>(null);
  const isAdmin = useHasRole("admin");
  const now = useMemo(() => new Date(), []);
  const yearOptions = useMemo(
    () => availableYears(now).map((y) => ({ label: String(y), value: y })),
    [now],
  );

  const {
    year,
    setYear,
    summary,
    loading,
    failure,
    recomputing,
    recomputeFailure,
    recompute,
    retry,
    refresh,
    patchStationPayout,
  } = useThgAnnualSummary({ initialYear: defaultYear(now), enabled: isAdmin });

  const notify = useCallback((notification: PayoutNotification) => {
    toast.current?.show({ ...notification, life: notification.severity === "success" ? 3000 : 5000 });
  }, []);

  // CLR-283 credit-note actions: one at a time for the whole page (research R2).
  const payouts = useThgPayoutActions({ year, refresh, patchStationPayout, notify });
  const payoutBusy = payouts.busy !== null;

  const handleRecompute = async () => {
    const recomputed = await recompute();
    if (recomputed) {
      toast.current?.show({ severity: "success", summary: "Success", detail: COPY.recomputed(year), life: 3000 });
    }
  };

  const renderBody = () => {
    if (!isAdmin || failure?.kind === "FORBIDDEN") {
      return <ThgFailureState kind="FORBIDDEN" year={year} />;
    }
    if (loading) {
      return (
        <div className="flex flex-column align-items-center gap-3 p-5" role="status">
          <ProgressSpinner style={{ width: "3rem", height: "3rem" }} strokeWidth="4" />
          <span className="text-color-secondary">{COPY.loading(year)}</span>
        </div>
      );
    }
    if (failure) {
      return <ThgFailureState kind={failure.kind} year={year} message={failure.message} onRetry={retry} />;
    }
    if (!summary) return null;

    const isFinal = summary.status === "FINAL";
    const showPayouts = hasPayoutData(summary);
    return (
      <>
        {showPayouts && payouts.confirm && (
          <PayoutConfirmDialog
            year={summary.year}
            confirm={payouts.confirm}
            onAccept={payouts.accept}
            onCancel={payouts.cancelConfirm}
          />
        )}
        <SummaryHeader
          summary={summary}
          actions={
            <>
              <ThgExportButton
                summary={summary}
                onError={(detail) =>
                  toast.current?.show({ severity: "error", summary: "Error", detail, life: 5000 })
                }
              />
              {showPayouts && (
                <SyncButton
                  syncing={payouts.busy?.kind === "sync"}
                  disabled={payoutBusy || recomputing}
                  onSync={payouts.sync}
                />
              )}
              {isFinal && (
                <RecomputeButton
                  year={summary.year}
                  recomputing={recomputing}
                  disabled={payoutBusy}
                  onRecompute={handleRecompute}
                />
              )}
            </>
          }
        />
        {recomputeFailure && (
          <Message
            severity="error"
            className="w-full justify-content-start mb-3"
            text={
              recomputeFailure.kind === "RATE_NOT_CONFIGURED"
                ? COPY.rateNotConfigured(summary.year)
                : recomputeFailure.message
            }
          />
        )}
        {payouts.syncNotice && (
          <Message
            severity={payouts.syncNotice.severity}
            className="w-full justify-content-start mb-3"
            text={payouts.syncNotice.text}
          />
        )}
        {!isFinal && <ProvisionalBanner />}
        <DataQualityNotes
          excludedSessionCount={summary.excludedSessionCount}
          stationsWithoutOwner={summary.stationsWithoutOwner}
          internalStationCount={(summary.stations ?? []).filter(isUnpaidInternal).length}
        />
        {summary.payoutTotals && <PayoutTotals totals={summary.payoutTotals} />}
        {showPayouts && (
          <div className="text-sm text-color-secondary mb-2" data-testid="thg-finalize-hint">
            <i className="pi pi-info-circle mr-1" aria-hidden="true" />
            {PAYOUT_COPY.finalizeInAccounting}
          </div>
        )}
        <ThgStationTable
          summary={summary}
          payoutActions={{
            busy: payouts.busy,
            locked: recomputing,
            onGenerate: payouts.requestGenerate,
            onPdf: payouts.openPdf,
          }}
        />
      </>
    );
  };

  return (
    <div className="layout-main">
      <div className="surface-section">
        <Toast ref={toast} className="z-10" />
        <div className="grid">
          <div className="col-12">
            <Card>
              <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mb-4">
                <div>
                  <div className="text-2xl font-semibold">THG Payouts</div>
                  <div className="text-color-secondary">
                    Each station owner's THG share for a calendar year, based on energy excluding free charging.
                  </div>
                </div>
                {isAdmin && (
                  <span className="flex align-items-center gap-3">
                    <ThgPricesButton onSaved={refresh} />
                    <label htmlFor="thg-year" className="font-semibold">
                      Year
                    </label>
                    <Dropdown
                      inputId="thg-year"
                      value={year}
                      options={yearOptions}
                      onChange={(e) => setYear(e.value)}
                      disabled={recomputing || payoutBusy}
                    />
                  </span>
                )}
              </div>
              {renderBody()}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThgAnnualSummary;
